<template>
  <tooltip
    ref="tippyRef"
    trigger="click"
    interactive
    placement="bottom-end"
    append-to="parent"
    theme="shadow"
    :arrow="false"
    :offset="[0, 4]"
    :on-show="onShow"
    :on-hide="onHide"
  >
    <button
      v-tooltip="$t('SHORTCUTS.REASSIGN_SHORTCUT')"
      :data-cy="`edit-${shortcut.key}`"
      class="flex size-6 items-center justify-center rounded text-secondary hover:bg-disabled hover:text-headline"
      :class="{ 'bg-surface-user-blue text-primary': isOpen }"
    >
      <icon-base :icon="IconEdit" width="16" height="16" view-box="0 0 12 12" />
    </button>
    <template #content>
      <div class="w-[280px] rounded bg-white">
        <div class="flex flex-col gap-2 px-4 pt-4">
          <h4 class="text-base font-semibold text-headline">
            {{ $t("SHORTCUTS.REASSIGN_SHORTCUT") }}
          </h4>
          <p class="text-sm text-label">
            {{
              $t("SHORTCUTS.REASSIGN_DESCRIPTION", {
                shortcut: shortcut.action,
              })
            }}
          </p>
        </div>
        <div class="flex flex-col gap-4 px-4 pt-2 pb-4">
          <input
            ref="inputRef"
            data-cy="reassign-input"
            readonly
            type="text"
            class="h-10 w-full rounded border px-4 py-2 text-sm text-headline placeholder-secondary focus:outline-none"
            :class="[
              errorMessage
                ? 'border-error'
                : 'border-divider focus:border-primary',
            ]"
            :placeholder="$t('SHORTCUTS.TYPE_TO_CUSTOMIZE')"
            :value="inputValue"
            @keydown="handleKeydown"
          />
          <p v-if="errorMessage" class="-mt-2 text-xs text-error">
            {{ errorMessage }}
          </p>
        </div>
        <div class="border-t border-divider p-4">
          <button
            data-cy="reassign-save"
            class="h-10 w-full rounded bg-primary text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-50"
            :disabled="!capturedShortcut"
            @click="handleSave"
          >
            {{ $t("COMMON.SAVE") }}
          </button>
        </div>
      </div>
    </template>
  </tooltip>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

import {
  useCustomizeKeyboardShortcuts,
  useKeyboardShortcuts,
} from "@/hooks/keyboardShortcuts";

import IconEdit from "@/components/icons/shortcuts/IconEdit.vue";
import IconBase from "@/components/ui/IconBase.vue";

import type {
  KeyboardShortcutUpdate,
  UserKeyboardShortcut,
} from "@/models/options";

const { shortcut } = defineProps<{ shortcut: UserKeyboardShortcut }>();

const emit = defineEmits<{
  save: [KeyboardShortcutUpdate];
}>();

const { t } = useI18n();
const { captureKeyboardShortcut } = useCustomizeKeyboardShortcuts();
const { configuredShortcuts } = useKeyboardShortcuts();

const tippyRef = useTemplateRef("tippyRef");
const inputRef = useTemplateRef("inputRef");
const pendingKeys = ref<string[]>([]);
const capturedShortcut = ref<KeyboardShortcutUpdate | null>(null);
const errorMessage = ref("");
const isOpen = ref(false);

const inputValue = computed(() => {
  if (pendingKeys.value.length) {
    return pendingKeys.value.join(" + ");
  }
  return capturedShortcut.value?.action ?? "";
});

const resetState = () => {
  pendingKeys.value = [];
  capturedShortcut.value = null;
  errorMessage.value = "";
};

const onShow = () => {
  isOpen.value = true;
  resetState();
  nextTick(() => {
    inputRef.value?.focus();
  });
};

const onHide = () => {
  isOpen.value = false;
  resetState();
};

const handleKeydown = (evt: KeyboardEvent) => {
  if (evt.key === "Escape") {
    evt.stopPropagation();
    tippyRef.value?.tippy?.hide();
    return;
  }
  evt.preventDefault();
  evt.stopPropagation();

  const {
    pending,
    action,
    action_string,
    keys,
    modifiers,
    hasConflict: conflict,
    isValid: valid,
    hasError: error,
  } = captureKeyboardShortcut(evt);

  if (error) {
    errorMessage.value = t("SHORTCUTS.INVALID_SHORTCUT", {
      shortcut: action,
    });
    capturedShortcut.value = null;
    pendingKeys.value = [];
    return;
  }

  if (pending) {
    pendingKeys.value = modifiers ?? [];
    capturedShortcut.value = null;
    errorMessage.value = "";
    return;
  }

  pendingKeys.value = [];

  if (!action || !action_string) {
    return;
  }

  const normalizeAction = (action: string) =>
    action.split(" + ").sort().join(" + ");
  const normalizedAction = normalizeAction(action);
  const existingShortcut = configuredShortcuts.value.find(
    ({ key, action }) =>
      key !== shortcut.key && normalizeAction(action) === normalizedAction
  );

  if (conflict || existingShortcut) {
    errorMessage.value = t("SHORTCUTS.ALREADY_IN_USE", {
      action: existingShortcut ? existingShortcut.action : action,
    });
  } else {
    errorMessage.value = "";
  }

  capturedShortcut.value = {
    key: shortcut.key,
    action,
    action_string,
    keys,
    modifiers,
    valid,
    conflict,
  } as KeyboardShortcutUpdate;
};

const handleSave = () => {
  if (!capturedShortcut.value) {
    return;
  }
  emit("save", capturedShortcut.value);
  tippyRef.value?.tippy?.hide();
};
</script>
